import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/helpers/axiosInstance';

const MESH_BASE_URL = process.env.MESH_BASE_URL;

const createPayment = async payload => {
  const response = await axiosInstance().post(
    `${MESH_BASE_URL}/v1/init/payment`,
    payload
  );

  return response.data;
};

export const useCreatePayment = () => {
  const queryClient = useQueryClient();

  return useMutation(createPayment, {
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['user-subscription'] })
  });
};

const checkSubscription = async () => {
  const response = await axiosInstance().get(
    `/api/v2/stripe_api/check_subscription`
  );

  return response.data;
};

export const useCheckSubscription = (email, enabled = true) => {
  return useQuery({
    queryKey: ['user-subscription', email],
    queryFn: () => checkSubscription(), // ✅ remove (email) here
    enabled: enabled && !!email,
    staleTime: 1000 * 60 * 10,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
};

const createCheckoutSession = async payload => {
  const response = await axiosInstance().post(
    `/v2/stripe_api/checkout`,
    payload
  );

  return response.data;
};

export const useCreateCheckoutSession = () => {
  const queryClient = useQueryClient();

  return useMutation(createCheckoutSession, {
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['user-subscription'] })
  });
};

const getSubscriptionToggle = async () => {
  const response = await axiosInstance().get(
    '/api/v2/stripe_api/flags?type=subscription_web'
  );
  return response.data.data;
  // return { subscription_web: true }
};

export const useGetSubscriptionToggle = (enabled = true) => {
  return useQuery({
    queryKey: ['subscription-feature-flag'],
    queryFn: getSubscriptionToggle,
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
};

export const useGetPromoCode = () => {
  return useMutation({
    mutationFn: async code => {
      const response = await axiosInstance().get(
        `/api/v2/stripe_api/promos/${code}`
      );
      return response.data.data;
    }
  });
};

const createCheckout = async payload => {
  const response = await axiosInstance().post(
    '/api/v2/stripe_api/checkout',
    payload
  );
  return response.data;
};

const createRenew = async payload => {
  const response = await axiosInstance().post(
    '/api/v2/stripe_api/renew',
    payload
  );
  return response.data.data;
}

export const useCreateCheckout = () => {
  return useMutation({
    mutationFn: createCheckout
  });
};

export const useCreateRenew = () => {
  return useMutation({
    mutationFn: createRenew
  });
};

const getTransactions = async () => {
  const res = await axiosInstance().get('/api/v2/stripe_api/transactions');
  return res.data.data || [];
};

export function useGetTransactions(enabled = true) {
  return useQuery({
    queryKey: ['transactions'],
    queryFn: getTransactions,
    enabled // Only fetch when subscription is active
  });
}

const cancelSubscription = async () => {
  const response = await axiosInstance().delete(
    '/api/v2/stripe_api/subscription'
  );

  return response.data;
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation(cancelSubscription, {
    onSuccess: () => {
      // Refetch subscription status after cancellation
      queryClient.invalidateQueries({ queryKey: ['user-subscription'] });
    }
  });
};
